/**
 * TourContext — Centralized state for the admin tour system.
 *
 * Architecture:
 *  - The AdminLayout wraps everything in a TourProvider
 *  - The AdminLayout displays a permanent "?" button in the header
 *  - Each admin page calls useRegisterTour(steps, pageKey) to declare its tour
 *  - When the user clicks "?", the currently registered tour launches
 *  - Each tour is auto-shown on first visit, then on-demand via the "?" button
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const TourContext = createContext({
  steps: null,
  pageKey: null,
  isOpen: false,
  register: () => {},
  unregister: () => {},
  launch: () => {},
  close: () => {},
  hasTour: false,
});

export const TourProvider = ({ children }) => {
  const [steps, setSteps] = useState(null);
  const [pageKey, setPageKey] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const register = useCallback((newSteps, newPageKey) => {
    setSteps(newSteps);
    setPageKey(newPageKey);
  }, []);

  const unregister = useCallback(() => {
    setSteps(null);
    setPageKey(null);
    setIsOpen(false);
  }, []);

  const launch = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback((markSeen = true) => {
    setIsOpen(false);
    if (markSeen && pageKey) {
      try { localStorage.setItem('remalex_tour_seen_' + pageKey, '1'); } catch {}
    }
  }, [pageKey]);

  return (
    <TourContext.Provider
      value={{
        steps,
        pageKey,
        isOpen,
        register,
        unregister,
        launch,
        close,
        hasTour: !!steps && steps.length > 0,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);

/**
 * Hook used by admin pages to register their tour.
 * Call once at the top of the page component.
 *
 * @param {Array} steps - Tour steps (cf. AdminTour for shape)
 * @param {string} pageKey - Unique key for this page (used for localStorage "seen" flag)
 * @param {boolean} autoStart - Auto-show on first visit (default true)
 */
export const useRegisterTour = (steps, pageKey, autoStart = true) => {
  const { register, unregister, launch } = useTour();
  const launchedRef = useRef(false);

  useEffect(() => {
    register(steps, pageKey);
    return () => unregister();
  }, [steps, pageKey, register, unregister]);

  // Auto-start on first visit (delayed so DOM is ready)
  useEffect(() => {
    if (!autoStart || !pageKey || launchedRef.current) return;
    let seen = false;
    try { seen = !!localStorage.getItem('remalex_tour_seen_' + pageKey); } catch {}
    if (seen) return;
    const t = setTimeout(() => {
      launch();
      launchedRef.current = true;
    }, 700);
    return () => clearTimeout(t);
  }, [pageKey, autoStart, launch]);
};
